import React from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useField } from 'formik';
import { styled } from '@mui/material/styles';
import { Box, Grid, Tooltip, InputLabel, FormHelperText } from '@mui/material';
import { PickersDay } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Calender } from 'components/SVG';
import { useSchemaContext } from 'sharedComponents/SparkForm/SparkForm';
import { useResize } from 'hooks/common';
import type { GridProps } from '@mui/material';
import type { DatePickerProps } from '@mui/x-date-pickers/DatePicker';

type SparkDatePickerProps = {
  label: string;
  name: string;
  size?: GridProps['xs'];
  containerStyle?: GridProps['sx'];
  helpText?: string;
  readonly?: boolean;
  hideHelperText?: boolean;
} & DatePickerProps<Dayjs>;

const SquarePickersDay = styled(PickersDay<Dayjs>)(() => ({
  borderRadius: '0px',
  outline: '1px solid #e4e7e7',
  margin: 0,
  width: '39px',
  height: '38px',
  ':hover': {
    backgroundColor: '#e4e7e7',
    color: 'inherit',
  },
  '&.Mui-selected': {
    backgroundColor: 'var( --color-primary)',
    border: '1px solid var( --color-primary-light)',
    color: '#fff',
  },
}));

function SparkDatePicker({
  label,
  name,
  size,
  containerStyle,
  helpText = '',
  readonly = false,
  hideHelperText = false,
  ...rest
}: SparkDatePickerProps) {
  const { isMobile } = useResize();
  const { validationSchema } = useSchemaContext();
  const [field, meta, helper] = useField<string>(name);

  const isError = meta.touched && !!meta.error;

  const isFieldRequired = React.useMemo(() => {
    try {
      const fieldSchema = validationSchema.describe().fields[name];
      // @ts-ignore
      const isRequiredSchema = fieldSchema.tests.find(
        //@ts-ignore
        (test) => test.name === 'requiredTest'
      );
      if (isRequiredSchema) {
        return true;
      }

      if ('optional' in fieldSchema) {
        return !fieldSchema.optional;
      }
      return false;
    } catch {
      return false;
    }
  }, [name, validationSchema]);

  const defaultSize = size || (isMobile ? 12 : 6);

  const handleChange = (date: Dayjs | null) => {
    if (date) {
      const formattedDate = date.format('MM-DD-YYYY');
      helper.setValue(formattedDate);
    } else {
      helper.setValue('');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Grid
        item
        xs={defaultSize}
        container
        direction='column'
        sx={containerStyle}
      >
        <Box>
          <InputLabel
            sx={{
              marginLeft: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              fontSize: 16,
              width: '100%',
            }}
          >
            <span>{isFieldRequired && '*'}</span>
            {label}
            {helpText && (
              <Tooltip
                title={helpText}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <InfoOutlinedIcon fontSize='small' />
              </Tooltip>
            )}
          </InputLabel>
          <DatePicker<Dayjs>
            disableHighlightToday={true}
            disableOpenPicker={readonly}
            disabled={readonly}
            onChange={handleChange}
            slots={{
              day: SquarePickersDay,
              openPickerIcon: Calender,
            }}
            slotProps={{
              layout: {
                sx: {
                  '& .MuiDateCalendar-root': {
                    height: '100%',
                    width: '100%',
                  },
                },
              },
              textField: {
                fullWidth: true,
                disabled: readonly,
                error: isError,
                InputProps: {
                  readOnly: readonly,
                },
              },
            }}
            value={field.value ? dayjs(field.value, 'MM-DD-YYYY') : null}
            {...rest}
          />
        </Box>
        {!hideHelperText && (
          <FormHelperText
            sx={{
              color: (theme) => theme.palette.error.main,
              marginLeft: 1,
              marginBottom: 0.5,
            }}
          >
            {isError ? meta.error : ' '}
          </FormHelperText>
        )}
      </Grid>
    </LocalizationProvider>
  );
}

export default SparkDatePicker;
